import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { of, Subject, switchMap, tap } from 'rxjs';
import { ConfigService } from '../config.service';
import { DataTransform, DateField } from './dataTransformFunctions';
import { McDataMapService } from './mc-data-map.service';
import { CustomerCredit, PostTripWalletPayment, PostWalletDonation, WalletCreditRecord, WalletTransaction, WalletTransactionRecord } from './types/finance';


export interface GetWalletCreditResponse {
  credits: WalletCreditRecord[]
  transactions: WalletTransactionRecord[]
}

export interface WalletReportDataParams {

  tripStart: Date
  tripEnd: Date
  expirationStart: Date
  expirationEnd: Date
  originationStart: Date
  originationEnd: Date
  locations?: string[]
  hideExpired?: boolean
  hideUsed?: boolean
  hideAvailable?: boolean
  hasFutureTrip?: boolean | null

}

export interface WalletUpdatedArgs {
  creditId: number,
  operation: "added" | "updated" | "removed"
}

export interface ApplyCreditParams {
  creditId: number,
  tripId: number | "other",
  note: string,
  sendEmail: boolean,
  custId: number
  amount: number
}
@Injectable({
  providedIn: 'root'
})
export class WalletService {

  private walletUpdated = new Subject<WalletUpdatedArgs>()
  walletUpdated$ = this.walletUpdated.asObservable()

  constructor(
    private http: HttpClient,
    private config: ConfigService,
    private mcDataMapService: McDataMapService
  ) { }

  private readonly customerCreditDateFields: DateField<CustomerCredit>[] = [
    { dateType: "central", fieldName: "originationDate" },
    { dateType: "central", fieldName: "expirationDate" },
  ]

  private readonly customerTransactionDateFields: DateField<WalletTransaction>[] = [
    { dateType: "central", fieldName: "amountPaidDate" }
  ]

  private readonly walletCreditReportDateFields: DateField<WalletCreditRecord>[] = [
    {dateType: "central", fieldName: "originationDate"},
    {dateType: "central", fieldName: "expirationDate"},
    {dateType: "local", fieldName: "tripDate"},
    
  ]

  private readonly walletCreditReportTransactionDateFields: DateField<WalletTransactionRecord>[] = [
    {dateType: "local", fieldName: "tripDate"},
    {dateType: "central", fieldName: "transferDate"}
  ]

  getCreditsForCustomer(customerId: number) {
    return this.http.get<CustomerCredit[]>(`${this.config.dataApiUrl}/customers/${customerId.toString()}/wallet/credits`).pipe(
      switchMap(credits => {
        return DataTransform.replaceDatesArray(credits, this.customerCreditDateFields)
      })
    )
  }

  getTransactionsForCustomer(customerId: number) {
    return this.http.get<WalletTransaction[]>(`${this.config.dataApiUrl}/customers/${customerId.toString()}/wallet/transactions`).pipe(
      switchMap(transactions => {
        return DataTransform.replaceDatesArray(transactions, this.customerTransactionDateFields)
      })
    )
  }

  
  updateCreditExpiration(creditId: number, expirationDate: Date) {

    const data = {
      expirationDate: DateTime.fromJSDate(expirationDate).startOf("day").toFormat("yyyy-MM-dd")
    }
    return this.http.patch(`${this.config.dataApiUrl}/customers/wallet-credits/${creditId}`, data).pipe(
      tap(result => this.walletUpdated.next({ creditId: creditId, operation: "updated" }))
    )
  }

  applyWalletPayment(tripId: number, params: PostTripWalletPayment) {
    return this.http.post(`${this.config.dataApiUrl}/trips/${tripId.toString()}/finance/wallet-payments`, params).pipe(
      tap(result => this.walletUpdated.next({ creditId: params.incomingWalletId, operation: "updated" }))

    )
  }

  walletCreditDonation(params: PostWalletDonation) {
    return this.http.post(`${this.config.dataApiUrl}/finance/wallet/donate`, params).pipe(
      tap(result => this.walletUpdated.next({ creditId: params.incomingWalletId, operation: "updated" }))
    )
  }

  walletReportData(params: WalletReportDataParams) {

    let getParams = new HttpParams()
      .set('tripStart', DateTime.fromJSDate(params.tripStart).startOf('day').toFormat('yyyy-MM-dd'))
      .set('tripEnd', DateTime.fromJSDate(params.tripEnd).endOf('day').toFormat('yyyy-MM-dd'))
      .set('expirationStart', DateTime.fromJSDate(params.expirationStart).startOf('day').toFormat('yyyy-MM-dd'))
      .set('expirationEnd', DateTime.fromJSDate(params.expirationEnd).endOf('day').toFormat('yyyy-MM-dd'))
      .set('originationStart', DateTime.fromJSDate(params.originationStart).startOf('day').toFormat('yyyy-MM-dd'))
      .set('originationEnd', DateTime.fromJSDate(params.originationEnd).endOf('day').toFormat('yyyy-MM-dd'))
      .set('locations', params.locations ? params.locations.join(',') : '')
      .set('hideExpired', params.hideExpired ? 'true' : 'false')
      .set('hideUsed', params.hideUsed ? 'true' : 'false')
      .set('hideAvailable', params.hideAvailable ? 'true' : 'false')

    if (params.hasFutureTrip != null) {
      getParams = getParams.set('hasFutureTrip', params.hasFutureTrip.toString())
    }

    return this.http.get<GetWalletCreditResponse>(`${this.config.dataApiUrl}/customers/wallet-credits`, {
      params: getParams
    }).pipe(
      switchMap(response => {

        return of({
          credits: DataTransform.replaceDatesArrayDirect(response.credits, this.walletCreditReportDateFields),
          transactions: DataTransform.replaceDatesArrayDirect(response.transactions, this.walletCreditReportTransactionDateFields)
        })
      
      })
    )
}

}
